import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronDown, FolderOpen, Plus, Settings2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import type { Portfolio } from '../../redux/api/portfolioApi';

interface PortfolioPickerProps {
  portfolios: Portfolio[];
  selectedPortfolioId: string | null;
  onSelect: (portfolioId: string | null) => void;
  onManage: () => void;
  showAllSitesOption?: boolean;
}

const PortfolioPicker: React.FC<PortfolioPickerProps> = ({
  portfolios,
  selectedPortfolioId,
  onSelect,
  onManage,
  showAllSitesOption = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const selectedPortfolio = portfolios.find(p => p.id === selectedPortfolioId);
  const label = selectedPortfolio?.name || (selectedPortfolioId ? 'Loading...' : 'All Sites');

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    } else {
      setPosition(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!buttonRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors"
        style={{
          background: selectedPortfolioId ? 'rgba(23, 196, 128, 0.1)' : 'var(--color-bg-tertiary)',
          color: selectedPortfolioId ? '#17c480' : 'var(--color-text-secondary)',
        }}
      >
        <FolderOpen className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate flex-1 text-left">{label}</span>
        <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
      </button>

      {isOpen && position && createPortal(
        <div
          ref={dropdownRef}
          className="fixed eg-dropdown z-[9999] py-1"
          style={{ top: position.top, left: position.left, width: position.width }}
        >
          {showAllSitesOption && (
            <button
              onClick={() => { onSelect(null); setIsOpen(false); }}
              className={`eg-dropdown-item text-xs ${!selectedPortfolioId ? 'eg-dropdown-item-selected' : ''}`}
            >
              All Sites
            </button>
          )}

          {portfolios.map(p => (
            <button
              key={p.id}
              onClick={() => { onSelect(p.id); setIsOpen(false); }}
              className={`eg-dropdown-item text-xs ${selectedPortfolioId === p.id ? 'eg-dropdown-item-selected' : ''}`}
            >
              <span className="truncate">{p.name}</span>
            </button>
          ))}

          {portfolios.length === 0 && (
            <div className="px-3 py-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              No portfolios yet
            </div>
          )}

          <div className="eg-divider my-1" />
          <button
            onClick={() => { onManage(); setIsOpen(false); }}
            className="eg-dropdown-item text-xs flex items-center gap-2"
          >
            <Settings2 className="w-3 h-3" />
            Manage Portfolios
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PortfolioPicker;
